<template>
    <div class="d-flex flex-column">
        <header
            class="d-flex text-light align-items-center px-16"
            :style="headerStyle"
        >
            <button-link
                :icon-size="32"
                class="mr-12"
                flip-icon="horizontal"
                icon="arrow-right-thin"
                theme="light"
                @click="popModal"
            />

            <span class="text-truncate">
                {{ name }}
            </span>

            <div class="d-flex align-items-center ml-auto">
                <button-link
                    v-if="showLikes"
                    ref="like"
                    :icon="liked ? 'heart-full' : 'heart'"
                    :theme="liked ? 'danger' : 'light'"
                    class="mlsp-16"
                    @click="like"
                />

                <button-link
                    v-if="showBookmarked"
                    ref="bookmark"
                    :icon="bookmarked ? 'bookmark-full' : 'bookmark'"
                    theme="light"
                    class="mlsp-16"
                    @click="bookmark"
                />

                <button-link
                    v-if="$mq.smPlus && downloadUrl"
                    ref="download"
                    icon="download-arrow"
                    :href="downloadUrl"
                    :download="name"
                    theme="light"
                    class="mlsp-16"
                />

                <button-link
                    v-if="removable && $mq.smPlus"
                    ref="remove"
                    icon="trash"
                    theme="light"
                    class="mlsp-16"
                    @click="remove"
                />

                <button-link
                    v-if="$mq.smPlus && resourceId && hasInfoPage"
                    ref="info"
                    icon="info"
                    :to="`/resources/${resourceId}`"
                    theme="light"
                    class="mlsp-16"
                />

                <button-link
                    v-if="showEllipsis"
                    ref="popup-button"
                    class="mlsp-16"
                    icon="more"
                    theme="light"
                    @click="showPopUp = !showPopUp"
                />

                <tool-tip
                    v-if="$mq.smPlus && showLikes"
                    name="like"
                >
                    {{
                        ucfirst(
                            social.liked
                                ? $t("common.dislike")
                                : $t("common.like")
                        )
                    }}
                </tool-tip>

                <tool-tip
                    v-if="$mq.smPlus && showBookmarked"
                    name="bookmark"
                >
                    {{
                        ucfirst(
                            social.bookmarked
                                ? $t("common.removeFromBookmarks")
                                : $t("common.addToBookmarks")
                        )
                    }}
                </tool-tip>

                <tool-tip
                    v-if="$mq.smPlus && downloadUrl"
                    name="download"
                >
                    {{ ucfirst($t("common.download")) }}
                </tool-tip>

                <tool-tip
                    v-if="$mq.smPlus && resourceId && hasInfoPage"
                    name="info"
                    placement="left"
                >
                    {{ ucfirst($t("common.moreInfo")) }}
                </tool-tip>
            </div>
        </header>

        <div
            class="d-flex flex-column justify-content-center"
            style="height: calc(100vh - 50px)"
            @click.self="popModal"
        >
            <the-player
                class="h-100"
                v-bind="$attrs"
                focus-on-mount
                @accessed="accessed"
                @close="popModal"
            />
        </div>

        <pop-up
            name="popup-button"
            :show="showPopUp"
            class="p-12"
            @hide="hidePopUp"
        >
            <ul class="text-dark mb-0 list-unstyled">
                <li
                    v-if="resourceId && hasInfoPage"
                    class="mtsp-12"
                >
                    <router-link
                        class="text-dark d-flex align-items-center"
                        :to="`/resources/${resourceId}`"
                    >
                        <v-icon
                            name="info"
                            :w="16"
                        />

                        <span class="ml-8">{{
                            ucfirst($t("common.information"))
                        }}</span>
                    </router-link>
                </li>

                <li
                    v-if="downloadUrl && $mq.xsMinus"
                    class="mtsp-12"
                >
                    <a
                        :href="downloadUrl"
                        download
                        class="d-flex align-items-center"
                    >
                        <v-icon
                            name="download-arrow"
                            :w="16"
                        />

                        <span class="ml-8">{{
                            ucfirst($t("common.download"))
                        }}</span>
                    </a>
                </li>

                <li v-if="removable && $mq.xsMinus">
                    <button-link
                        theme="dark"
                        :bold="false"
                        @click="remove"
                    >
                        <v-icon
                            name="trash"
                            :w="16"
                        />

                        <span class="ml-8">
                            {{ ucfirst($t("common.delete")) }}
                        </span>
                    </button-link>
                </li>
            </ul>
        </pop-up>
    </div>
</template>

<script>
import { KEY_CODES, HEADER_HEIGHT } from "~/lib/constants"
import { ucfirst } from "~/lib/utils"
import { useListenerManager } from "front-toolkit/plugins/ListenersPlugin"

import ButtonLink from "~/components/buttons/ButtonLink"
import PopUp from "~/components/PopUp"
import ThePlayer from "./ThePlayer"
import ToolTip from "~/components/ToolTip"

export default {
    inject: ["popModal"],

    props: {
        name: String,
        downloadUrl: String,
        removable: Boolean,
        resourceId: null,
        social: Object,
        hasInfoPage: Boolean,
    },

    emits: ["accessed", "like", "bookmark", "remove"],

    setup() {
        const { listen } = useListenerManager()
        return { listen }
    },

    data() {
        return {
            showPopUp: null,
            liked: null,
            bookmarked: null,
        }
    },

    computed: {
        headerStyle() {
            return {
                height: `${HEADER_HEIGHT}px`,
            }
        },

        showEllipsis() {
            return (
                this.$mq.sMinus &&
                (this.resourceId || this.downloadUrl || this.removable)
            )
        },

        showLikes() {
            return typeof this.social?.liked === "boolean"
        },

        showBookmarked() {
            return typeof this.social?.bookmarked === "boolean"
        },
    },

    mounted() {
        this.listen("keydown", event => {
            if (event.keyCode == KEY_CODES.ESC) {
                this.popModal()
            }
        })
    },

    created() {
        if (this.social) {
            this.liked = this.social.liked
            this.bookmarked = this.social.bookmarked
        }
    },

    methods: {
        accessed() {
            this.$emit("accessed")
        },

        hidePopUp(event) {
            const ref = this.$refs["popup-button"].$el
            const tgt = event.target

            if (ref == tgt || ref.contains(tgt)) return

            this.showPopUp = false
        },

        async like() {
            this.$emit("like")
            this.liked = !this.liked
        },

        async bookmark() {
            this.$emit("bookmark")
            this.bookmarked = !this.bookmarked
        },

        remove() {
            this.$emit("remove")
            this.popModal()
        },

        ucfirst,
    },

    components: {
        ButtonLink,
        PopUp,
        ThePlayer,
        ToolTip,
    },
}
</script>
